<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { onClickOutside } from '@vueuse/core'
  import { requiredPlanForBookFormat } from '@/helpers/product'
  import { useDraftsMenuStore } from '@/stores/list-item-menu'
  import { useDraftsStore } from '@/stores/drafts'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { usePopupStore } from '@/stores/popup'
  import { useLoaderStore } from '@/stores/loader'
  import { useModalStore } from '@/stores/modal'
  import { useUserStore } from '@/stores/user'
  import { useRemoteImportStore } from '@/stores/remote-import'
  import { useRouter } from 'vue-router'
  import { useCreatorStore } from '@/stores/creator'
  import { useAmazonCoversStore } from '@/stores/amazon-covers.ts'
  import { usePrintBooksStore } from '@/stores/print-books'
  import { useKindleBooksStore } from '@/stores/kindle-books'
  import { useAudiobooksStore } from '@/stores/audiobooks'
  import { useUpgradeStore } from '@/stores/upgrade'
  import type { TUser } from '@/types/user'
  import type { TDraft } from '@/types/drafts'
  import type { TDropdownOption } from '@/types/dropdown'
  import type { TBookFormat } from '@/components/modal/DesModalChooseBookFormat.vue'
  import DesMenu from '@/components/menu/DesMenu.vue'
  import DesMenuOptions from '@/components/menu/DesMenuOptions.vue'
  import DesMenuItem from '@/components/menu/DesMenuItem.vue'
  import DesMenuSeparator from '@/components/menu/DesMenuSeparator.vue'

  const loader = useLoaderStore()
  const menu = useDraftsMenuStore()
  const covers = useAmazonCoversStore()
  const drafts = useDraftsStore()
  const popup = usePopupStore()
  const modal = useModalStore()
  const snack = useSnackbarStore()
  const router = useRouter()
  const user = useUserStore()
  const remoteImports = useRemoteImportStore()
  const creator = useCreatorStore()
  const printBooks = usePrintBooksStore()
  const kindleBooks = useKindleBooksStore()
  const audiobooks = useAudiobooksStore()
  const upgrade = useUpgradeStore()

  const target = ref<HTMLElement | null>(null)
  onClickOutside(target, (event) => {
    if ((event.target as HTMLElement)?.closest('[menu-toggle-ignore]')) return

    if (menu.item) menu.close()
  })

  // The store's `flip` only reserves room for the first-level menu; the submenu
  // needs room for menu + submenu, so decide its side from the actual space right.
  const submenuOpenLeft = computed(() => {
    if (menu.flip) return false

    return menu.position.left - window.scrollX + 500 > window.innerWidth
  })

  const typeLabel = computed(() => drafts.getTypeLabel(menu.item?.type))
  const typeLabelCapitalized = computed(() => typeLabel.value.charAt(0).toUpperCase() + typeLabel.value.slice(1))

  const edit = () => {
    const from = 'context-menu'
    if (!menu.item?.id || !menu.item?.type) {
      throw new Error('item is invalid')
    }
    drafts.goToEditDraft(menu.item.id, menu.item.type, from)
  }

  async function pickTemplate() {
    if (!menu?.item?.id) return

    loader.show('Getting data...')
    const draft: TDraft | undefined = await drafts.get(menu.item.id)
    loader.hide()

    if (!draft) {
      snack.add(`Error getting ${typeLabel.value}`, { type: 'danger' })
      return
    }

    let tags: number[] | null = null
    if (draft.tags?.length === 0 && draft.id) {
      tags = await setTags(true)

      if (tags === null) {
        return
      }
    }

    creator.tagsListDropdown = creator.tagsDropdownHints.filter((t: TDropdownOption) =>
      (tags ?? draft.tags)?.includes(Number(t.name)),
    )
    await remoteImports.setNewProjectData(draft.id, draft?.name ?? 'New Project', draft?.content ?? '', draft.tags)
    router.push('/creator/templates')
  }

  function isFormatEnabled(format: TBookFormat): boolean {
    if (format === 'printbook') return (user.accountType?.printbook ?? 0) > 0
    if (format === 'kindle') return (user.accountType?.ebook_export ?? 0) > 0
    if (format === 'audiobook') return (user.accountType?.tts_credits ?? 0) > 0
    if (format === 'presentation') return user.canCreatePresentations()

    return true
  }

  async function turnInto(format: TBookFormat) {
    const item = menu.item

    if (!item?.id) return

    if (!isFormatEnabled(format)) {
      menu.close()
      upgrade.showUpgradeModal('draft-context-menu', requiredPlanForBookFormat(format))

      return
    }

    if (format === 'ebook') {
      await pickTemplate()

      return
    }

    if (format === 'presentation') {
      menu.close()
      await router.push({ path: '/creator/presentation-outline', query: { draftId: item.id } })

      return
    }

    menu.close()
    loader.show()

    try {
      let id: number | null = null

      if (format === 'printbook') id = await printBooks.createFromDraft(item.id)
      else if (format === 'kindle') id = await kindleBooks.createFromDraft(item.id)
      else if (format === 'audiobook') id = await audiobooks.createFromDraft(item.id)

      const path = format === 'printbook' ? 'print-book' : format === 'kindle' ? 'kindle-book' : 'audiobook'

      if (id) router.push(`/docs/${path}/${id}`)
    } catch {
      snack.add(`Error creating ${format}`, { type: 'danger' })
    } finally {
      loader.hide()
    }
  }

  async function duplicate() {
    if (!menu.item) return

    const res = await modal.open<{ name: string; duplicateCover: boolean } | null>(
      () => import('@/components/modal/DesModalDuplicateDoc.vue'),
      {
        draft: menu.item,
      },
    )

    if (!res?.name) {
      return
    }
    loader.show()
    try {
      const response = await drafts.duplicate(menu.item, res.name, res.duplicateCover)
      loader.hide()
      if (res.duplicateCover && response.print_book?.amazon_cover_id) {
        loader.show('Copying cover...')
        await covers.generateThumbnailById(response.print_book.amazon_cover_id)
        loader.hide()
      }
      if (!response) {
        snack.add('Name must be unique')
      }
    } catch {
      loader.hide()
      snack.add(`Error duplicating ${typeLabel.value}!`)
    }
  }

  async function remove() {
    if (!menu.item) return

    const result = await popup.confirm(`Are you sure you want to delete this ${typeLabel.value}?`, {
      content: 'This action is irreversible.',
      labelCancel: 'Cancel',
      labelConfirm: 'Delete',
    })

    if (result !== false) {
      await drafts.remove(menu.item)
    }
  }

  async function setTags(inFlow: boolean = false): Promise<number[] | null> {
    if (!menu?.item) return null

    const res = await modal.open<number[] | null>(() => import('@/components/modal/DesModalDraftTags.vue'), {
      draft: menu.item,
      inFlow,
    })
    res && (menu.item.tags = res)
    return res
  }

  async function fileInformation() {
    if (!menu?.item) {
      return
    }

    loader.show('Getting data...')
    const authorUser: TUser | null = await user.getUserById(menu.item.user_id)

    try {
      await user.fetchCompanyUsers()
    } catch {
      // ignore
    }

    loader.hide()

    modal.open(() => import('@/components/modal/DesModalFileInfo.vue'), {
      item: menu.item,
      draft: menu.item,
      draftShares: user.companyUsers,
      isDraft: true,
      authorUser,
    })
  }
</script>

<template>
  <div
    class="absolute z-50"
    :style="{ left: menu.position.left + 'px', top: menu.position.top + 'px' }"
    ref="target"
    @click="menu.close()"
  >
    <DesMenuOptions :open="menu.opened" :left="menu.flip" :right="!menu.flip">
      <DesMenu :left="submenuOpenLeft" :right="!submenuOpenLeft" v-if="menu.item?.type === 'Standard'">
        <template #toggle>
          <DesMenuItem more icon="fa-light fa-right-left">Turn into</DesMenuItem>
        </template>

        <DesMenuItem
          image="/dashboard/new-project/type-ebook.png"
          description="PDF eBook, Flipbook or Live eBook"
          @click="turnInto('ebook')"
        >
          eBook
        </DesMenuItem>
        <DesMenuItem
          image="/dashboard/new-project/type-kindle.png"
          description="Designed for digital reading"
          :unlocked-for="isFormatEnabled('kindle') ? undefined : requiredPlanForBookFormat('kindle')"
          @click="turnInto('kindle')"
        >
          Kindle Book
        </DesMenuItem>
        <DesMenuItem
          image="/dashboard/new-project/type-audiobook.png"
          description="Stories come alive vocally"
          :unlocked-for="isFormatEnabled('audiobook') ? undefined : requiredPlanForBookFormat('audiobook')"
          @click="turnInto('audiobook')"
        >
          Audiobook
        </DesMenuItem>
        <DesMenuItem
          image="/dashboard/new-project/type-printbook.png"
          description="Standalone print book creation"
          :unlocked-for="isFormatEnabled('printbook') ? undefined : requiredPlanForBookFormat('printbook')"
          @click="turnInto('printbook')"
        >
          Print Book
        </DesMenuItem>
        <DesMenuItem
          image="/dashboard/new-project/type-presentation.png"
          description="Create a slide presentation"
          :unlocked-for="isFormatEnabled('presentation') ? undefined : requiredPlanForBookFormat('presentation')"
          @click="turnInto('presentation')"
        >
          Presentation
        </DesMenuItem>
      </DesMenu>

      <DesMenuItem @click="edit()" icon="fa-light fa-pen">Edit {{ typeLabel }}</DesMenuItem>
      <DesMenuItem
        @click="pickTemplate()"
        icon="fa-light fa-file-image"
        v-if="menu.item?.type === 'Standard' && typeLabel !== 'manuscript'"
      >
        Choose a template
      </DesMenuItem>
      <DesMenuItem @click="setTags()" icon="fa-light fa-tags" v-if="menu.item?.type === 'Standard'">Themes</DesMenuItem>
      <DesMenuItem @click="duplicate()" icon="fa-light fa-copy">Duplicate</DesMenuItem>
      <DesMenuItem @click="fileInformation()" icon="fa-light fa-circle-info">
        {{ typeLabelCapitalized }} information
      </DesMenuItem>
      <DesMenuSeparator />
      <DesMenuItem @click="remove()" icon="fa-light fa-trash-can" danger>Delete</DesMenuItem>
    </DesMenuOptions>
  </div>
</template>
